import React, {
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react"
import { TableHeader } from "../../../../../../Common/Table/TableHeader"
import { Table, TableColumn } from "resha"
import {
    APIJobModel,
    getJobID,
    APIJobQueryModel
} from "../../../../../../../Models/API/JobModel"
import { FilterExpression } from "../../../../../../../Models/Client/FilterExpression"
import { SortExpression } from "../../../../../../../Models/Client/SortExpression"
import { Menu } from "dash-styled"
import { FlatButton } from "../../../../../../Common/FlatButton/FlatButton"
import { NothingMatched } from "../../../../../../Common/Empty/NothingMatched"
import {
    SortChooserProps,
    SortOptionField
} from "../../../../../../Common/Button/SortChooser"
import {
    defaultJobFilterOnAdd,
    unreadMessageClass
} from "../../Common/constants"
import { FilterFields } from "../../../../../../Common/FilterComposer/FilterComposer"
import { APIJobsPerStatusModel } from "../../../../../../../Models/API/APIJobsPerStatusModel"
import {
    hasAppPermission,
    ProjectRole
} from "../../../../../../../Models/Client/ProjectRole"
import { FetchState } from "../../../../../../../Models/Client/FetchState"
import { useVisibleTableRows } from "../../../../../../../Hooks/useVisibleTableRows"

export interface ColumnsProps<M extends APIJobModel> {
    orgUsername: string
    appID: string
    sort: SortExpression<APIJobQueryModel>
    onReview?: (item: M, isChecked: boolean) => void
    activeLearningStatusFetch: FetchState<"active" | "inactive" | "unused">
}

interface Props<M extends APIJobModel> {
    orgUsername: string
    appID: string
    items?: M[]
    totalCount?: number
    filteredCount?: number
    filteredCountByStatus: APIJobsPerStatusModel | undefined
    filters: FilterExpression<APIJobQueryModel>[]
    sort?: SortExpression<APIJobQueryModel>
    selectedDataIds: string[]
    filterFields: FilterFields<APIJobQueryModel & { output: unknown }>
    sortFields: SortOptionField<APIJobQueryModel>[]
    role: ProjectRole[]
    onDownload?: () => void
    onCancel?: () => void
    onRelabel?: () => void
    onEnqueue?: () => void
    onDelete?: () => void
    onAddTags?: () => void
    onRemoveTags?: () => void
    onLabel?: () => void
    onReview?: (item: APIJobModel, isChecked: boolean) => void
    onToggleRowCheck: (item: APIJobModel) => void
    onRowClick: (item: APIJobModel, isChecked: boolean) => void
    onLoadMore: () => Promise<void>
    onChangeFilter: (value: FilterExpression<APIJobQueryModel>[]) => void
    onChangeSort: (value: SortExpression<APIJobQueryModel>) => void
    onVisibleItemsChanged?: (itemIds: string[]) => void
    isAppEnabled?: boolean
    columns: (vals: ColumnsProps<M>) => TableColumn<M>[]
    subHeader?: ReactNode
    noFilterLink?: string
    activeLearningStatusFetch: FetchState<"active" | "inactive" | "unused">
    fixedLayout: boolean
}

export const JobsListTable = <M extends APIJobModel>({
    orgUsername,
    items,
    totalCount,
    filteredCount,
    filteredCountByStatus,
    onChangeFilter,
    onChangeSort,
    filters,
    sort = { sortBy: "created", desc: false },
    selectedDataIds,
    filterFields,
    sortFields,
    role,
    onToggleRowCheck,
    onDownload,
    onCancel,
    onRelabel,
    onReview,
    onDelete,
    onLoadMore,
    onRowClick,
    onEnqueue,
    appID,
    isAppEnabled,
    onAddTags,
    onRemoveTags,
    onLabel,
    onVisibleItemsChanged,
    columns,
    subHeader,
    noFilterLink,
    activeLearningStatusFetch,
    fixedLayout
}: Props<M>) => {
    const scrollableRef = useRef<HTMLDivElement>(null)
    const visibleJobIds = useVisibleTableRows(scrollableRef, items)
    const actionTarget =
        selectedDataIds.length > 0
            ? "selected"
            : filters.length > 0
            ? "filtered"
            : "all"
    const sortOptions: SortChooserProps<APIJobQueryModel> = useMemo(
        () => ({
            fields: sortFields,
            applied: sort,
            onChange: onChangeSort
        }),
        [sortFields, sort, onChangeSort]
    )
    const filterOptions = useMemo(
        () => ({
            fields: filterFields,
            applied: filters,
            onChange: onChangeFilter,
            defaultOnAdd: defaultJobFilterOnAdd
        }),
        [filters, onChangeFilter, filterFields]
    )

    const loadingLabel = {
        value: "Loading...",
        label: <span className="text-smoke-350">Loading...</span>
    }
    const [actions, setActions] = useState<
        [
            (
                | {
                      value: string
                      label: ReactNode
                  }
                | string
            ),
            (() => void) | undefined
        ][]
    >([[loadingLabel, undefined]])

    const handleExtraAction = useCallback(
        (label: string) => {
            const action = actions.find(a => {
                const item = a[0]
                if (typeof item === "string") {
                    return item === label
                } else {
                    return item.value === label
                }
            })

            if (action !== undefined) {
                action[1]?.()
            }
        },
        [actions]
    )
    const handleExtraActionOpen = useMemo(() => {
        if (filteredCountByStatus === undefined) {
            return undefined
        }

        return () => {
            const newActions = ([] as [string, () => void][]).concat(
                onLabel === undefined
                    ? []
                    : [[`Label ${actionTarget}`, onLabel]],
                onCancel === undefined
                    ? []
                    : [[`Cancel ${actionTarget}`, onCancel]],
                onRelabel === undefined
                    ? []
                    : [[`Reprocess ${actionTarget}`, onRelabel]],
                onEnqueue === undefined
                    ? []
                    : [[`Queue ${actionTarget}`, onEnqueue]],
                onAddTags === undefined
                    ? []
                    : [[`Add tags to ${actionTarget}`, onAddTags]],
                onRemoveTags === undefined
                    ? []
                    : [[`Remove tags from ${actionTarget}`, onRemoveTags]],
                onDelete === undefined
                    ? []
                    : [[`Delete ${actionTarget}`, onDelete]]
            )

            setActions(
                newActions.length > 0
                    ? newActions
                    : [
                          [
                              {
                                  value: "No action available",
                                  label: (
                                      <span className="text-smoke-350">
                                          No action available
                                      </span>
                                  )
                              },
                              () => {}
                          ]
                      ]
            )
        }
    }, [
        filteredCountByStatus,
        onLabel,
        actionTarget,
        onCancel,
        onRelabel,
        onEnqueue,
        onAddTags,
        onRemoveTags,
        onDelete
    ])

    const extraActions = useMemo(() => {
        if (!isAppEnabled) {
            return undefined
        }
        return (
            <>
                {hasAppPermission(role, "job:download") && (
                    <FlatButton
                        onClick={onDownload}
                        className="ml-2"
                        disabled={onDownload === undefined}
                        testId="job-list-download"
                    >
                        Download {actionTarget}
                    </FlatButton>
                )}
                <Menu
                    className="ml-2"
                    title="Other actions"
                    align="right"
                    testId="job-list-extra-actions"
                    nonInteractive={actions.every(
                        ([, perform]) => perform === undefined
                    )}
                    items={actions.map(([label]) => label)}
                    onChoose={handleExtraAction}
                    onOpen={handleExtraActionOpen}
                />
            </>
        )
    }, [
        isAppEnabled,
        role,
        onDownload,
        actionTarget,
        actions,
        handleExtraAction,
        handleExtraActionOpen
    ])

    const renderEmpty = () => (
        <NothingMatched messageVariant="try" noFilterLink={noFilterLink} />
    )
    const cols = useMemo(
        () =>
            columns({
                orgUsername,
                appID,
                sort,
                onReview,
                activeLearningStatusFetch
            }),
        [columns, orgUsername, appID, sort, onReview, activeLearningStatusFetch]
    )

    useEffect(() => {
        onVisibleItemsChanged?.(visibleJobIds)
    }, [onVisibleItemsChanged, visibleJobIds])

    return (
        <>
            <TableHeader
                totalCount={totalCount}
                sortOptions={sortOptions}
                filterOptions={filterOptions}
                extraActions={extraActions}
                filteredCount={filteredCount}
            />
            {subHeader}
            <div
                ref={scrollableRef}
                className="flex-grow flex-shrink flex-basis-0 overflow-auto -m-6 mt-0 px-6"
            >
                <Table<M>
                    columns={cols}
                    items={items}
                    itemCount={filteredCount}
                    getKey={getJobID}
                    checkedItemIds={selectedDataIds}
                    onToggleRowCheck={onToggleRowCheck}
                    onRowClick={onRowClick}
                    loadMore={onLoadMore}
                    className={`w-full h-0 min-h-0 mt-0 ${
                        fixedLayout ? "table-fixed" : ""
                    }`}
                    renderEmpty={renderEmpty}
                    noFilterLink={noFilterLink}
                    extractJobRowClassName={unreadMessageClass}
                    stickyHeader={true}
                />
            </div>
        </>
    )
}
